import { useId, useState } from "react";
import { findRivalry } from "@averagejoesff/shared";
import type { MatchupReport, WeeklyMatchup } from "@averagejoesff/shared";
import { FactList } from "./FactList";
import { MatchupHeader } from "./MatchupHeader";
import { RivalryHistory, RivalrySummary } from "./RivalryPanel";
import type { ManagerLookup } from "./managers";
import { matchupAnchorId } from "./series";

/** This week's facts shown before the card is expanded; the rest wait behind the toggle. */
const PREVIEW_FACT_COUNT = 3;

interface MatchupCardProps {
  matchup: WeeklyMatchup;
  report: MatchupReport;
  managers: ManagerLookup;
}

/**
 * One game on this week's slate. Collapsed, it answers "what's the story
 * this week?" — both teams, this week's top angles, the series record and
 * recent form. Expanding adds the rest of this week's facts, the key games
 * and the lasting history, so six cards stay skimmable.
 */
export function MatchupCard({ matchup, report, managers }: MatchupCardProps) {
  const [expanded, setExpanded] = useState(false);
  const detailsId = useId();
  const [leftId, rightId] = matchup.managerIds;
  const rivalry = findRivalry(report, leftId, rightId);

  const weekFacts = expanded ? matchup.facts : matchup.facts.slice(0, PREVIEW_FACT_COUNT);
  const hiddenItemCount =
    Math.max(0, matchup.facts.length - PREVIEW_FACT_COUNT) +
    (rivalry ? rivalry.keyGames.length + rivalry.facts.length : 0);

  return (
    <article className="matchup-card" id={matchupAnchorId(matchup.managerIds)}>
      <MatchupHeader left={managers.get(leftId)} right={managers.get(rightId)} />
      {(rivalry?.nickname || matchup.headline) && (
        <div className="matchup-card__billing">
          {rivalry?.nickname && <span className="matchup-card__nickname">{rivalry.nickname}</span>}
          {matchup.headline && <span className="matchup-card__headline">{matchup.headline}</span>}
        </div>
      )}
      <FactList facts={weekFacts} heading="This week" />
      {rivalry ? (
        <RivalrySummary rivalry={rivalry} managers={managers} />
      ) : (
        <p className="matchup-card__first-meeting">First-ever meeting.</p>
      )}
      {expanded && rivalry && (
        <div id={detailsId}>
          <RivalryHistory rivalry={rivalry} managers={managers} />
        </div>
      )}
      {hiddenItemCount > 0 && (
        <button
          type="button"
          className="matchup-card__toggle"
          aria-expanded={expanded}
          aria-controls={expanded ? detailsId : undefined}
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? "Show less" : "Full history"}
          <span className="matchup-card__toggle-icon" aria-hidden="true">
            {expanded ? "▴" : "▾"}
          </span>
        </button>
      )}
    </article>
  );
}
